import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * 테마 모드 타입
 */
export type ThemeMode = 'light' | 'dark'

/**
 * 테마 색상 구조
 * (사용자 커스터마이징 가능)
 */
export interface ThemeColors {
  primary: {
    50: string
    100: string
    500: string
    600: string
    700: string
  }
}

/**
 * 프리셋 테마 정의
 *
 * 사용자가 설정 화면에서 선택할 수 있는 기본 테마들
 */
export const THEME_PRESETS = {
  blue: {
    primary: {
      50: '#f0f9ff',
      100: '#e0f2fe',
      500: '#0ea5e9',
      600: '#0284c7',
      700: '#0369a1',
    },
  },
  purple: {
    primary: {
      50: '#faf5ff',
      100: '#f3e8ff',
      500: '#a855f7',
      600: '#9333ea',
      700: '#7e22ce',
    },
  },
  green: {
    primary: {
      50: '#f0fdf4',
      100: '#dcfce7',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
    },
  },
} as const

export type ThemePreset = keyof typeof THEME_PRESETS

/**
 * 테마 스토어 인터페이스
 */
interface ThemeStore {
  // 상태
  mode: ThemeMode
  preset: ThemePreset
  customColors: ThemeColors | null

  // 액션
  setMode: (mode: ThemeMode) => void
  setPreset: (preset: ThemePreset) => void
  setCustomColors: (colors: ThemeColors) => void
  resetTheme: () => void
}

/**
 * 테마 상태 관리 스토어 (Zustand)
 *
 * localStorage에 자동으로 저장되어 브라우저를 닫았다 열어도 설정이 유지됩니다.
 *
 * @example
 * ```tsx
 * import { useThemeStore } from '@/core/theme/themeStore'
 *
 * function ThemeSettings() {
 *   const { preset, setPreset } = useThemeStore()
 *
 *   return (
 *     <button onClick={() => setPreset('purple')}>
 *       퍼플 테마 적용
 *     </button>
 *   )
 * }
 * ```
 */
export const useThemeStore = create<ThemeStore>()(
  persist(
    (set) => ({
      mode: 'light',
      preset: 'blue',
      customColors: null,

      setMode: (mode) => {
        set({ mode })
        applyThemeMode(mode)
      },

      setPreset: (preset) => {
        set({ preset, customColors: null })
        applyThemeColors(THEME_PRESETS[preset])
      },

      setCustomColors: (colors) => {
        set({ customColors: colors })
        applyThemeColors(colors)
      },

      resetTheme: () => {
        set({ mode: 'light', preset: 'blue', customColors: null })
        applyThemeMode('light')
        applyThemeColors(THEME_PRESETS.blue)
      },
    }),
    {
      name: 'theme-storage', // localStorage key
    }
  )
)

/**
 * 테마 모드 적용 (다크 모드)
 *
 * document.documentElement에 'dark' 클래스를 추가/제거하여
 * index.css의 .dark 선택자를 활성화합니다.
 */
function applyThemeMode(mode: ThemeMode) {
  if (mode === 'dark') {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}

/**
 * 테마 색상 적용 (CSS 변수 업데이트)
 *
 * Tailwind v4에서 @theme로 정의된 CSS 변수를 동적으로 변경하여
 * 모든 컴포넌트에 즉시 반영합니다.
 *
 * v4에서는 Hex 형식을 직접 사용할 수 있으므로 RGB 변환이 불필요합니다.
 * 리렌더링 없이 색상만 변경되므로 성능상 이점이 있습니다.
 */
function applyThemeColors(colors: ThemeColors) {
  const root = document.documentElement

  // Tailwind v4는 Hex 값을 직접 받을 수 있음 (RGB 변환 불필요)
  root.style.setProperty('--color-primary-50', colors.primary[50])
  root.style.setProperty('--color-primary-100', colors.primary[100])
  root.style.setProperty('--color-primary-500', colors.primary[500])
  root.style.setProperty('--color-primary-600', colors.primary[600])
  root.style.setProperty('--color-primary-700', colors.primary[700])
}

/**
 * 테마 초기화 (앱 시작 시 호출)
 *
 * main.tsx에서 React 렌더링 전에 호출하여
 * localStorage에 저장된 테마를 즉시 적용합니다.
 *
 * @example
 * ```tsx
 * // main.tsx
 * import { initializeTheme } from '@/core/theme/themeStore'
 *
 * initializeTheme()
 *
 * createRoot(document.getElementById('root')!).render(<App />)
 * ```
 */
export function initializeTheme() {
  const store = useThemeStore.getState()
  applyThemeMode(store.mode)
  applyThemeColors(store.customColors || THEME_PRESETS[store.preset])
}
